import { useMutation, useQuery } from '@apollo/client';
import type { LabelStrikeMapping } from '@communities/content-management/domain';
import { GET_STRIKE_CONFIGURATION, UPDATE_STRIKE_CONFIGURATION } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import type { CommunityWorkflowConfiguration } from '@core/graphql/types/schema';
import { track } from '@core/logging';
import { useCallback, useMemo, useState } from 'react';

interface StrikeConfigurationResponse {
  community: {
    id: string;
    workflowConfigurations: CommunityWorkflowConfiguration[];
  } | null;
}

interface UpdateStrikeConfigurationResponse {
  upsertCommunityWorkflowConfiguration: CommunityWorkflowConfiguration;
}

export interface StrikeConfiguration {
  rollingWindowDays: number;
  labelStrikeMappings: LabelStrikeMapping[];
}

export interface UpdateStrikeConfigurationInput {
  rollingWindowDays: number;
  labelStrikeMappings: LabelStrikeMapping[];
}

const DEFAULT_ROLLING_WINDOW_DAYS = 30;

export function useStrikeConfiguration(communityId?: string) {
  const [mutationErrors, setMutationErrors] = useState<DomainError[] | null>(null);

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<StrikeConfigurationResponse>(GET_STRIKE_CONFIGURATION, {
    variables: { communityId },
    fetchPolicy: 'cache-first',
    skip: !communityId,
  });

  const [updateMutation, { loading: mutationLoading }] = useMutation<UpdateStrikeConfigurationResponse>(UPDATE_STRIKE_CONFIGURATION, {
    refetchQueries: communityId ? [{ query: GET_STRIKE_CONFIGURATION, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const queryErrors: DomainError[] | null = useMemo(() => {
    if (!queryError) return null;

    return transformErrors(queryError);
  }, [queryError]);

  const errors = mutationErrors || queryErrors;

  const configuration: StrikeConfiguration = useMemo(() => {
    const workflows = data?.community?.workflowConfigurations || [];
    const strikeConfig = workflows.find(wf => wf.type === 'strike');

    return {
      rollingWindowDays: strikeConfig?.rollingWindowDays ?? DEFAULT_ROLLING_WINDOW_DAYS,
      labelStrikeMappings: strikeConfig?.labelStrikeMappings || [],
    };
  }, [data]);

  const resetErrors = useCallback(() => {
    setMutationErrors(null);
  }, []);

  const updateConfiguration = async (input: UpdateStrikeConfigurationInput): Promise<boolean> => {
    if (!communityId) return false;

    try {
      track('UpdateStrikeConfigurationInitiated', {
        communityId,
        rollingWindowDays: String(input.rollingWindowDays),
        mappingsCount: String(input.labelStrikeMappings.length),
      });

      setMutationErrors(null);
      await updateMutation({
        variables: {
          communityId,
          type: 'strike',
          input: {
            rollingWindowDays: input.rollingWindowDays,
            labelStrikeMappings: input.labelStrikeMappings,
          },
        },
      });

      track('UpdateStrikeConfigurationSucceeded', {
        communityId,
        rollingWindowDays: String(input.rollingWindowDays),
        mappingsCount: String(input.labelStrikeMappings.length),
      });

      return true;
    } catch (error) {
      track('UpdateStrikeConfigurationFailed', {
        communityId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setMutationErrors(transformErrors(error));

      return false;
    }
  };

  return {
    configuration,
    loading: queryLoading || mutationLoading,
    errors,
    updateConfiguration,
    refetch,
    resetErrors,
  };
}
